// Owner-only aggregate analytics. The real access control is server-side (see
// supabase/migrations/0002_owner_metrics.sql / 0003_owner_metrics_charts.sql — the RPC
// checks auth.uid() against the server-only app_owner table). This module only decides
// whether to *show the entry point*; if a non-owner somehow reaches it, the RPC call
// below fails and they see "Access denied".

import { reportError } from '../monitoring.js';

function escapeHTML(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[ch]));
}

const CARD_DEFS = [
  { key: 'total_verified_users', label: 'Total verified users', def: 'Accounts with a confirmed email address, all-time.' },
  { key: 'weekly_active_users', label: 'Weekly active users', def: 'Distinct users with at least one active day in the trailing 7 days.' },
  { key: 'monthly_active_users', label: 'Monthly active users', def: 'Distinct users with at least one active day in the trailing 30 days.' },
  { key: 'avg_completed_tests_per_verified_user', label: 'Avg. completed scored tests / user', def: 'Total completed scored attempts ÷ total verified users.' },
  { key: 'retention_7d_pct', label: '7-day retention', def: 'Of users who joined 7+ days ago, % active again in the last 7 days.', suffix: '%', cohortKey: 'retention_7d_cohort_size' },
  { key: 'retention_30d_pct', label: '30-day retention', def: 'Of users who joined 30+ days ago, % active again in the last 30 days.', suffix: '%', cohortKey: 'retention_30d_cohort_size' },
];

function formatValue(value, suffix) {
  if (value === null || value === undefined) return '—';
  return `${value}${suffix || ''}`;
}

function formatShortDate(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

// Thin rounded bars anchored to a baseline, native <title> for hover value (no JS
// interactivity needed), muted non-highlighted bars so the selected period reads clearly.
function dailyBarChart(series, { highlightCount, height = 120, barColor = 'var(--lime-2)', mutedColor = '#e4e9ee' } = {}) {
  const width = 640;
  const padding = 4;
  const slot = (width - padding * 2) / series.length;
  const barWidth = Math.max(2, slot * 0.62);
  const max = Math.max(1, ...series.map((point) => point.count));
  const baseline = height - 2;
  const highlightFrom = series.length - highlightCount;

  const bars = series.map((point, index) => {
    const barHeight = Math.max(point.count > 0 ? 3 : 1, (point.count / max) * (height - 14));
    const x = padding + index * slot + (slot - barWidth) / 2;
    const y = baseline - barHeight;
    const isHighlighted = index >= highlightFrom;
    return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barWidth.toFixed(1)}" height="${barHeight.toFixed(1)}" rx="${Math.min(3, barWidth / 2).toFixed(1)}" fill="${isHighlighted ? barColor : mutedColor}"><title>${formatShortDate(point.date)}: ${point.count}</title></rect>`;
  }).join('');

  return `<svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="none" role="img" aria-label="Daily trend, last ${series.length} days">
    <line x1="${padding}" y1="${baseline + 0.5}" x2="${width - padding}" y2="${baseline + 0.5}" stroke="var(--line)" stroke-width="1" />
    ${bars}
  </svg>`;
}

function horizontalBarChart(rows, { color = 'var(--lime-2)' } = {}) {
  if (!rows.length) return '<p class="owner-empty">No completed scored attempts yet.</p>';
  const max = Math.max(1, ...rows.map((r) => r.attempts));
  return `<div class="owner-hbar-list">${rows.map((row) => {
    const pct = Math.max(4, Math.round((row.attempts / max) * 100));
    return `
      <div class="owner-hbar-row">
        <span class="owner-hbar-label">${escapeHTML(row.module_key)}</span>
        <div class="owner-hbar-track"><div class="owner-hbar-fill" style="width:${pct}%; background:${color}"></div></div>
        <span class="owner-hbar-value">${row.attempts}</span>
      </div>`;
  }).join('')}</div>`;
}

export function createOwnerDashboard({ container, supabase, onExit }) {
  let status = 'loading'; // loading | ready | denied | error
  let metrics = null;
  let signupPeriod = 'month'; // today | week | month

  function renderState(title, body) {
    container.innerHTML = `
      <div class="owner-shell">
        <div class="owner-header">
          <h1 class="owner-title">Owner analytics</h1>
          <button class="owner-back" data-action="exit">Back to dashboard</button>
        </div>
        <div class="owner-state"><strong>${escapeHTML(title)}</strong>${escapeHTML(body)}</div>
      </div>
    `;
  }

  function periodHeadline() {
    if (signupPeriod === 'today') return { value: metrics.new_verified_today, label: 'New verified sign-ups today', highlightCount: 1 };
    if (signupPeriod === 'week') return { value: metrics.new_verified_this_week, label: 'New verified sign-ups this week', highlightCount: 7 };
    return { value: metrics.new_verified_this_month, label: 'New verified sign-ups this month', highlightCount: 30 };
  }

  function render() {
    if (status === 'loading') return renderState('Loading…', 'Fetching aggregate metrics.');
    if (status === 'denied') return renderState('Access denied', 'This account is not authorized to view owner analytics.');
    if (status === 'error') return renderState('Could not load metrics', 'Something went wrong fetching aggregate metrics. Try again shortly.');

    const cards = CARD_DEFS.map((cfg) => {
      const value = formatValue(metrics[cfg.key], cfg.suffix);
      const cohortNote = cfg.cohortKey ? ` (cohort: ${metrics[cfg.cohortKey] ?? 0} users)` : '';
      return `
        <div class="owner-card">
          <div class="owner-card-value">${escapeHTML(value)}</div>
          <div class="owner-card-label">${escapeHTML(cfg.label)}</div>
          <div class="owner-card-def">${escapeHTML(cfg.def + cohortNote)}</div>
        </div>
      `;
    }).join('');

    const headline = periodHeadline();
    const signupsChart = dailyBarChart(metrics.signups_daily || [], { highlightCount: headline.highlightCount, barColor: 'var(--lime-2)' });
    const activeChart = dailyBarChart(metrics.active_daily || [], { highlightCount: 30, barColor: 'var(--blue)' });
    const moduleUsage = horizontalBarChart(metrics.module_usage || []);

    container.innerHTML = `
      <div class="owner-shell">
        <div class="owner-header">
          <h1 class="owner-title">Owner analytics</h1>
          <button class="owner-back" data-action="exit">Back to dashboard</button>
        </div>
        <p class="owner-subtitle">Aggregate usage metrics only — no individual answers or personal data are shown here.</p>
        <div class="owner-grid">${cards}</div>

        <div class="owner-chart-card">
          <div class="owner-chart-head">
            <div>
              <div class="owner-chart-value">${headline.value ?? '—'}</div>
              <div class="owner-chart-label">${escapeHTML(headline.label)}</div>
            </div>
            <select class="owner-period-select" data-role="signup-period">
              <option value="today" ${signupPeriod === 'today' ? 'selected' : ''}>Today</option>
              <option value="week" ${signupPeriod === 'week' ? 'selected' : ''}>This Week</option>
              <option value="month" ${signupPeriod === 'month' ? 'selected' : ''}>This Month</option>
            </select>
          </div>
          <div class="owner-chart-def">Verified sign-ups, trailing 30 days. Bars in the selected period are highlighted; the headline number uses calendar day/week/month, so it may differ slightly from the highlighted rolling-window bars.</div>
          ${signupsChart}
        </div>

        <div class="owner-chart-card">
          <div class="owner-chart-head">
            <div>
              <div class="owner-chart-value">${metrics.weekly_active_users ?? '—'}</div>
              <div class="owner-chart-label">Active users, trailing 30 days</div>
            </div>
          </div>
          <div class="owner-chart-def">Distinct users active each day, over the last 30 days.</div>
          ${activeChart}
        </div>

        <div class="owner-usage-card">
          <h2 class="owner-usage-title">Module usage — completed scored attempts, all modules with at least one</h2>
          ${moduleUsage}
        </div>
      </div>
    `;
  }

  async function load() {
    status = 'loading';
    render();
    const { data, error } = await supabase.rpc('owner_metrics');
    if (error) {
      const denied = error.code === '42501' || /not authorized/i.test(error.message || '');
      status = denied ? 'denied' : 'error';
      if (!denied) reportError(error, { op: 'owner_metrics' });
      render();
      return;
    }
    metrics = data;
    status = 'ready';
    render();
  }

  container.addEventListener('click', (event) => {
    const button = event.target.closest('[data-action="exit"]');
    if (button) onExit();
  });

  container.addEventListener('change', (event) => {
    if (event.target.dataset.role === 'signup-period') {
      signupPeriod = event.target.value;
      render();
    }
  });

  return { load, render };
}
